import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { get } from "@/lib/api-client";
import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody } from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatDateTime, formatRelative } from "@/lib/format";
import type { Notification } from "@/types/notification";

export function NotificationsAdminPage() {
  const [tab, setTab] = useState("dead_letter");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-notifications", tab],
    queryFn: () => get<Notification[]>("/admin/notifications", { status: tab }),
    refetchInterval: tab === "dead_letter" ? 30_000 : false,
  });

  const retry = useMutation({
    mutationFn: (id: number) => AdminAPI.retryNotification(id),
    onSuccess: () => {
      toast.success("Retry requested");
      qc.invalidateQueries({ queryKey: ["admin-notifications"] });
    },
  });

  const rows = data ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Notifications</h1>
        <p className="text-sm text-surface-500 mt-1">
          Cross-user notification delivery. Retry Dead Letter items after fixing upstream issues.
        </p>
      </header>

      <Card>
        <div className="px-5 pt-3">
          <Tabs value={tab} onChange={setTab}
                tabs={[
                  { value: "dead_letter", label: "Dead Letter" },
                  { value: "failed",      label: "Failed"      },
                  { value: "queued",      label: "Queued"      },
                  { value: "delivered",   label: "Delivered"   },
                ]} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Bell} title="No notifications in this view" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>ID</TH><TH>Category</TH><TH>Channel</TH>
                  <TH>Subject</TH><TH>Attempts</TH><TH>Status</TH>
                  <TH>Created</TH><TH></TH>
                </TR>
              </THead>
              <tbody>
                {rows.map((n) => (
                  <TR key={n.id}>
                    <TD>{n.id}</TD>
                    <TD>
                      <Badge tone="neutral" className="capitalize">
                        {n.category.replace(/_/g, " ")}
                      </Badge>
                    </TD>
                    <TD className="uppercase text-2xs font-semibold">{n.channel}</TD>
                    <TD className="text-xs max-w-xs truncate">{n.subject}</TD>
                    <TD>{n.attempts}</TD>
                    <TD><StatusPill status={n.status} /></TD>
                    <TD className="text-2xs text-surface-500">{formatRelative(n.created_at)}</TD>
                    <TD>
                      {n.status === "dead_letter" && (
                        <Button size="sm" variant="outline"
                                leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                                loading={retry.isPending}
                                onClick={() => retry.mutate(n.id)}>
                          Retry
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
